import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Cat, Eye, EyeOff, ArrowLeft, Lock } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(password);
      toast.success(t('login_success'));
      navigate('/app');
    } catch (error) {
      toast.error(t('login_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24 py-12 bg-stone-50 relative">
        {/* Language selector top right */}
        <div className="absolute top-6 right-6">
          <LanguageSelector variant="compact" />
        </div>
        
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-orange-600 mb-8 transition-colors">
            <ArrowLeft size={16} />
            {t('login_back')}
          </Link>

          <div className="mb-8">
            <img 
              src="/logo.jpg" 
              alt="Gats del Carrer" 
              className="h-16 w-auto"
              data-testid="login-logo"
            />
          </div>

          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-4">
              <Lock size={12} />
              {t('login_badge')}
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900 mb-2">
              {t('login_title')}
            </h1>
            <p className="text-stone-500">{t('login_description')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-stone-700">
                {t('login_password')}
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login_placeholder')}
                  required
                  autoFocus
                  className="rounded-xl border-stone-200 bg-stone-50 focus:border-orange-500 focus:ring-orange-500/20 pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-orange-600 hover:bg-orange-700 hover:-translate-y-0.5 transition-all py-6 text-sm font-bold"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('login_loading')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Cat size={18} />
                  {t('login_submit')}
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 auth-background relative">
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-2xl font-bold mb-2">{t('login_slogan_title')}</h2>
          <p className="text-stone-200">{t('login_slogan_description')}</p>
        </div>
      </div>
    </div>
  );
}
